import styled from 'styled-components'
import {
  StyledLegalNoticeProps,
  StyledIconWrapperProps,
  StyledContentProps,
} from './LegalNotice.types'

const getVariantColors = (variant: string, theme: any) => {
  switch (variant) {
    case 'warning':
      return {
        background: `rgba(255, 149, 0, 0.1)`, // 10% opacity warning background
        border: theme.colors.warning,
        icon: theme.colors.warning,
      }
    case 'info':
      return {
        background: `rgba(0, 173, 230, 0.1)`, // 10% opacity info background
        border: theme.colors.primary,
        icon: theme.colors.primary,
      }
    case 'success':
      return {
        background: `rgba(0, 179, 65, 0.1)`, // 10% opacity success background
        border: theme.colors.success,
        icon: theme.colors.success,
      }
    case 'danger':
      return {
        background: `rgba(255, 59, 48, 0.1)`, // 10% opacity danger background
        border: theme.colors.danger,
        icon: theme.colors.danger,
      }
    default:
      return {
        background: theme.colors.background.secondary,
        border: theme.colors.border.default,
        icon: theme.colors.text.secondary,
      }
  }
}

export const StyledLegalNotice = styled.div<StyledLegalNoticeProps>`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme, $size }) =>
    $size === 'compact' ? theme.spacing.small : theme.spacing.default};
  padding: ${({ theme, $size }) =>
    $size === 'compact'
      ? `${theme.spacing.small} ${theme.spacing.default}`
      : `${theme.spacing.default} ${theme.spacing.medium}`};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  border: 1px solid
    ${({ theme, $variant }) => getVariantColors($variant, theme).border};
  background-color: ${({ theme, $variant }) =>
    getVariantColors($variant, theme).background};
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  transition: ${({ theme }) => theme.transitions.default};
  position: relative;
  width: 100%;
  box-sizing: border-box;

  /* Mobile responsive adjustments */
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme, $size }) =>
      $size === 'compact'
        ? `${theme.spacing.small} ${theme.spacing.small}`
        : `${theme.spacing.default} ${theme.spacing.default}`};
    gap: ${({ theme }) => theme.spacing.small};
  }

  /* Ensure proper text rendering */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`

export const StyledIconWrapper = styled.div<StyledIconWrapperProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: ${({ $size }) => ($size === 'compact' ? '1px' : '2px')};

  svg {
    color: ${({ theme, $variant }) => getVariantColors($variant, theme).icon};
    width: ${({ $size }) => ($size === 'compact' ? '16px' : '18px')};
    height: ${({ $size }) => ($size === 'compact' ? '16px' : '18px')};
  }

  /* Mobile responsive icon sizing */
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    svg {
      width: ${({ $size }) => ($size === 'compact' ? '14px' : '16px')};
      height: ${({ $size }) => ($size === 'compact' ? '14px' : '16px')};
    }
  }
`

export const StyledContent = styled.div<StyledContentProps>`
  flex: 1;
  font-size: ${({ theme, $size }) =>
    $size === 'compact'
      ? theme.typography.fontSize.small
      : theme.typography.fontSize.body};
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};

  /* Ensure text wraps properly */
  word-wrap: break-word;
  word-break: break-word;

  /* Handle nested elements styling */
  p {
    margin: 0;

    &:not(:last-child) {
      margin-bottom: ${({ theme }) => theme.spacing.small};
    }
  }

  strong {
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  }

  em {
    font-style: italic;
  }

  a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }

    &:focus {
      outline: 2px solid ${({ theme }) => theme.colors.primary};
      outline-offset: 2px;
      border-radius: ${({ theme }) => theme.borderRadius.small};
    }
  }

  /* Mobile responsive font sizing */
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme, $size }) =>
      $size === 'compact'
        ? theme.typography.fontSize.micro
        : theme.typography.fontSize.small};
  }
`
